import React, { useCallback, useEffect, useState } from 'react';
import { useDashboardLocale } from '../shared/dashboardLocale';
import type { DashboardMessageKey } from '../shared/dashboardMessages';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { kiwoomH3, kiwoomMuted } from './kiwoomTheme';
import { todayKSTDate } from './kiwoomTodayResult';

/** mercury-analyser-kiwoom read API (SSH proxy to trader snapshots). */
export function kiwoomPairsPaperApiUrl(tradeDate?: string): string {
  const base = '/mercury-analyser-kiwoom/pairs-paper';
  if (!tradeDate) return base;
  return `${base}?date=${encodeURIComponent(tradeDate)}`;
}

/** @deprecated S3 path — OCI paper no longer publishes here; use kiwoomPairsPaperApiUrl. */
export function kiwoomPairsPaperS3Url(tradeDate?: string): string {
  const d = tradeDate || todayKSTDate();
  return `/kiwoom-live/pairs-paper/${d}.json`;
}

export type KiwoomPairsPairState = {
  pair_id?: string;
  PairID?: string;
  side?: number;
  Side?: number;
  z?: number;
  Z?: number;
  hold_days?: number;
  HoldDays?: number;
};

export type KiwoomPairsPosition = {
  qty: number;
  basis_krw?: number;
};

export type KiwoomPairsFill = {
  time: string;
  symbol: string;
  side: string;
  price: number;
  quantity: number;
  krw?: number;
  fee_krw?: number;
  pnl_krw?: number;
};

export type KiwoomPairsPaperSnapshot = {
  date?: string;
  mode?: string;
  equity_krw?: number;
  cash_krw?: number;
  realized_pnl?: number;
  pair_states?: KiwoomPairsPairState[];
  target_shares?: Record<string, number>;
  positions?: Record<string, KiwoomPairsPosition>;
  fills?: KiwoomPairsFill[];
  updated_at?: string;
};

const PAIR_LABEL_KEYS: Record<string, DashboardMessageKey> = {
  'bank-kb-shinhan': 'kiwoom.pairs.label.bankKbShinhan',
  'bank-kb-hana': 'kiwoom.pairs.label.bankKbHana',
  'bank-shinhan-hana': 'kiwoom.pairs.label.bankShinhanHana',
  'telecom-skt-kt': 'kiwoom.pairs.label.telecomSktKt',
};

function pairLabel(
  id: string,
  t: (key: DashboardMessageKey) => string,
): string {
  const key = PAIR_LABEL_KEYS[id];
  return key ? t(key) : id;
}

const th: React.CSSProperties = {
  textAlign: 'left',
  color: '#a371f7',
  fontSize: '0.7rem',
  padding: '6px 8px',
  borderBottom: '1px solid #7c3aed44',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: '#f3e8ff',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #7c3aed22',
  verticalAlign: 'top',
};

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div className="kiwoom-pairs-stat-item">
      <div className="kiwoom-pairs-stat-label">{label}</div>
      <div className="kiwoom-pairs-stat-value" style={{ color: color ?? '#f3e8ff' }}>{value}</div>
    </div>
  );
}

function pairIdOf(s: KiwoomPairsPairState): string {
  return s.pair_id || s.PairID || '';
}

function sideOf(s: KiwoomPairsPairState): number {
  return s.side ?? s.Side ?? 0;
}

function zOf(s: KiwoomPairsPairState): number | null {
  const z = s.z ?? s.Z;
  return z == null || Number.isNaN(z) ? null : z;
}

function holdOf(s: KiwoomPairsPairState): number {
  return s.hold_days ?? s.HoldDays ?? 0;
}

function sideLabel(side: number): { text: string; color: string } {
  if (side > 0) return { text: 'LONG A', color: '#3fb950' };
  if (side < 0) return { text: 'SHORT A', color: '#f85149' };
  return { text: 'FLAT', color: kiwoomMuted };
}

function formatKrw(
  n: number | null | undefined,
  t: (key: DashboardMessageKey, params?: Record<string, string | number>) => string,
): string {
  if (n == null || Number.isNaN(n)) return '–';
  return t('kiwoom.currency.krw', { n: Math.round(n).toLocaleString('ko-KR') });
}

async function fetchPairsPaper(tradeDate?: string): Promise<KiwoomPairsPaperSnapshot | null> {
  const url = kiwoomPairsPaperApiUrl(tradeDate);
  const res = await fetch(url, { cache: 'no-store' });
  if (res.status === 404) return null;
  const text = await res.text();
  if (!res.ok || !text.trim() || text.trimStart().startsWith('<')) return null;
  return JSON.parse(text) as KiwoomPairsPaperSnapshot;
}

/** Latest snapshot from analyser (falls back to dated lookup if needed). */
async function fetchLatestPairsPaper(fromDate: string): Promise<{ snap: KiwoomPairsPaperSnapshot | null; date: string }> {
  const latest = await fetchPairsPaper();
  if (latest) {
    return { snap: latest, date: latest.date || fromDate };
  }
  const start = new Date(`${fromDate}T12:00:00+09:00`);
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start.getTime() - i * 86400000);
    const ymd = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
    const snap = await fetchPairsPaper(ymd);
    if (snap) return { snap, date: ymd };
  }
  return { snap: null, date: fromDate };
}

const KiwoomPairsPaperPanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [data, setData] = useState<KiwoomPairsPaperSnapshot | null>(null);
  const [asOf, setAsOf] = useState<string>(todayKSTDate());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { snap, date } = await fetchLatestPairsPaper(todayKSTDate());
      setData(snap);
      setAsOf(date);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pairs = data?.pair_states ?? [];
  const fills = data?.fills ?? [];
  const openCount = pairs.filter((p) => sideOf(p) !== 0).length;
  const realized = data?.realized_pnl ?? 0;

  return (
    <div
      className="kiwoom-pairs-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #7c3aed66',
        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(0,0,0,0.35) 100%)',
      }}
    >
      <div className="binance-alert-header">
        <div className="binance-alert-header-copy">
          <h3 style={{ ...kiwoomH3, marginTop: 0, marginBottom: 4 }}>Pairs Paper</h3>
          <div style={{ color: kiwoomMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
            {t('kiwoom.pairs.subtitle.line1')}
            <br />
            {t('kiwoom.pairs.subtitle.line2')}
            <br />
            {t('kiwoom.pairs.subtitle.line3')}
          </div>
        </div>
        <button
          type="button"
          className="kiwoom-pairs-refresh"
          onClick={() => { setLoading(true); load(); }}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading && !data && <div style={{ color: kiwoomMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>}
      {error && (
        <div style={{ color: '#f85149', fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}
      {!loading && !data && !error && (
        <div style={{ color: kiwoomMuted, fontSize: '0.78rem' }}>
          {t('kiwoom.pairs.noSnapshot')}
        </div>
      )}

      {data && (
        <>
          <div className="kiwoom-pairs-stats">
            <Stat label="as of" value={data.date ?? asOf} />
            <Stat label="equity" value={formatKrw(data.equity_krw, t)} />
            <Stat label="cash" value={formatKrw(data.cash_krw, t)} />
            <Stat label="open" value={openCount} color="#f0b90b" />
            <Stat
              label="realized"
              value={`${realized >= 0 ? '+' : ''}${formatKrw(realized, t)}`}
              color={realized >= 0 ? '#3fb950' : '#f85149'}
            />
            <Stat label="mode" value={data.mode ?? 'paper'} />
            <Stat label="pairs" value={pairs.length} />
            <Stat label="fills" value={fills.length} />
            <Stat
              label="updated"
              value={data.updated_at
                ? data.updated_at.slice(11, 16)
                : '–'}
            />
          </div>

          <div className="kiwoom-pairs-section-title">{t('kiwoom.pairs.section.pairs')}</div>
          <div className="binance-alert-table-wrap">
            <table key={`pairs-${locale}`} className="binance-alert-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: '36%' }}>{t('kiwoom.pairs.table.pair')}</th>
                  <th style={{ ...th, width: '22%' }}>{t('kiwoom.pairs.table.side')}</th>
                  <th style={{ ...th, width: '22%' }}>{t('kiwoom.pairs.table.z')}</th>
                  <th style={{ ...th, width: '20%' }}>{t('kiwoom.pairs.table.hold')}</th>
                </tr>
              </thead>
              <tbody>
                {pairs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...td, color: kiwoomMuted, textAlign: 'center' }}>{t('kiwoom.pairs.table.pairs.empty')}</td>
                  </tr>
                )}
                {pairs.map((p) => {
                  const id = pairIdOf(p);
                  const side = sideLabel(sideOf(p));
                  const z = zOf(p);
                  const hold = holdOf(p);
                  return (
                    <tr key={id || JSON.stringify(p)} className={sideOf(p) !== 0 ? 'broker-row-open' : undefined}>
                      <td style={{ ...td, wordBreak: 'break-word' }}>
                        <ThreeLineStack
                          mutedColor={kiwoomMuted}
                          lines={[pairLabel(id, t) || id || '–', id || null, null]}
                        />
                      </td>
                      <td style={{ ...td, color: side.color, fontWeight: 600 }}>
                        <ThreeLineStack mutedColor={kiwoomMuted} lines={[side.text, null, null]} />
                      </td>
                      <td style={td}>
                        <ThreeLineStack
                          mutedColor={kiwoomMuted}
                          lines={[z != null ? z.toFixed(2) : '–', null, null]}
                        />
                      </td>
                      <td style={td}>
                        <ThreeLineStack
                          mutedColor={kiwoomMuted}
                          lines={[hold > 0 ? `${hold}d` : String(hold), null, null]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="kiwoom-pairs-section-title" style={{ marginTop: 14 }}>{t('kiwoom.pairs.section.fillsToday')}</div>
          <div className="binance-alert-table-wrap">
            <table key={`fills-${locale}`} className="binance-alert-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: '28%' }}>{t('kiwoom.pairs.table.symbol')}</th>
                  <th style={{ ...th, width: '18%' }}>{t('kiwoom.pairs.table.side')}</th>
                  <th style={{ ...th, width: '26%' }}>{t('kiwoom.pairs.table.qty')}</th>
                  <th style={{ ...th, width: '28%' }}>{t('common.table.pnl')}</th>
                </tr>
              </thead>
              <tbody>
                {fills.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...td, color: kiwoomMuted, textAlign: 'center' }}>{t('kiwoom.pairs.table.fills.empty')}</td>
                  </tr>
                )}
                {fills.map((f, i) => (
                  <tr key={`${f.symbol}-${f.time}-${i}`}>
                    <td style={td}>
                      <ThreeLineStack
                        mutedColor={kiwoomMuted}
                        lines={[
                          f.symbol,
                          f.time
                            ? new Date(f.time).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
                            : null,
                          null,
                        ]}
                      />
                    </td>
                    <td style={td}>
                      <ThreeLineStack mutedColor={kiwoomMuted} lines={[f.side, null, null]} />
                    </td>
                    <td style={td}>
                      <ThreeLineStack
                        mutedColor={kiwoomMuted}
                        lines={[
                          f.quantity,
                          f.price != null ? f.price.toFixed(0) : null,
                          f.fee_krw != null ? `fee ${formatKrw(f.fee_krw, t)}` : null,
                        ]}
                      />
                    </td>
                    <td style={td}>
                      <ThreeLineStack
                        mutedColor={kiwoomMuted}
                        lines={[
                          f.pnl_krw != null ? (
                            <span style={{ color: f.pnl_krw >= 0 ? '#3fb950' : '#f85149' }}>
                              {f.pnl_krw >= 0 ? '+' : ''}{formatKrw(f.pnl_krw, t)}
                            </span>
                          ) : '–',
                          f.krw != null ? formatKrw(f.krw, t) : null,
                          null,
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default KiwoomPairsPaperPanel;
